import { API_BASE_URL, UnauthorizedError } from './client';
import {
  AssistantChatCitation,
  AssistantChatRequest,
  AssistantChatUsage,
} from '../types/Assistant';

export type AssistantStreamEvent =
  | { kind: 'citations'; citations: AssistantChatCitation[] }
  | { kind: 'delta'; text: string }
  | { kind: 'toolStart'; toolName: string; displayLabelHe: string }
  | { kind: 'toolEnd'; toolName: string; ok: boolean }
  | { kind: 'done'; usage: AssistantChatUsage; assistantLogId: number }
  | { kind: 'error'; message: string };

/**
 * POSTs to /api/assistant/chat/stream and yields SSE events as they arrive.
 * The server emits `citations` first, then a series of `delta` events with text
 * fragments, then a single `done`. On error: a `done` is NOT emitted; an
 * `error` event arrives and the stream ends.
 */
export async function* streamAssistantChat(
  request: AssistantChatRequest,
  signal?: AbortSignal,
): AsyncGenerator<AssistantStreamEvent, void, void> {
  const res = await fetch(`${API_BASE_URL}/api/assistant/chat/stream`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
    body: JSON.stringify(request),
    signal,
  });

  if (res.status === 401) throw new UnauthorizedError('/api/assistant/chat/stream');
  if (!res.ok) {
    throw new Error(`Stream failed: ${res.status} ${res.statusText}`);
  }
  if (!res.body) throw new Error('Stream has no body');

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // SSE event delimiter is "\n\n". Drain every complete event from the buffer.
      let sep = buffer.indexOf('\n\n');
      while (sep !== -1) {
        const raw = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSseEvent(raw);
        if (parsed) yield parsed;
        sep = buffer.indexOf('\n\n');
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseSseEvent(raw: string): AssistantStreamEvent | null {
  let eventName = '';
  let dataLine = '';
  for (const line of raw.split('\n')) {
    if (line.startsWith('event:')) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith('data:')) {
      // multi-line `data:` could be concatenated with '\n' per the spec, but
      // our backend always writes a single line, so keep it simple.
      dataLine = line.slice(5).trim();
    }
  }
  if (!eventName) return null;

  let payload: unknown = null;
  try {
    payload = dataLine ? JSON.parse(dataLine) : {};
  } catch {
    return null;
  }
  const p = payload as Record<string, unknown>;

  switch (eventName) {
    case 'citations':
      return { kind: 'citations', citations: (p.citations as AssistantChatCitation[]) ?? [] };
    case 'delta':
      return { kind: 'delta', text: typeof p.text === 'string' ? p.text : '' };
    case 'toolStart':
      return {
        kind: 'toolStart',
        toolName: typeof p.toolName === 'string' ? p.toolName : '',
        displayLabelHe: typeof p.displayLabelHe === 'string' ? p.displayLabelHe : '',
      };
    case 'toolEnd':
      return {
        kind: 'toolEnd',
        toolName: typeof p.toolName === 'string' ? p.toolName : '',
        ok: Boolean(p.ok),
      };
    case 'done':
      return {
        kind: 'done',
        usage: {
          inputTokens: Number(p.inputTokens ?? 0),
          outputTokens: Number(p.outputTokens ?? 0),
          retrievedChunks: Number(p.retrievedChunks ?? 0),
        },
        assistantLogId: Number(p.assistantLogId ?? 0),
      };
    case 'error':
      return { kind: 'error', message: typeof p.message === 'string' ? p.message : 'Unknown error' };
    default:
      return null;
  }
}
